import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, LoadingSkeleton, EmptyState } from "@/components/Layout";
import { SeverityBadge, StatusPill } from "@/components/Badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, ShieldAlert, X, Download, Upload, Table2, LayoutGrid, Columns, Star, StarOff, AlertOctagon, Save, Trash2, Check, AlertCircle, Rocket } from "lucide-react";
import RiskFormDialog from "@/components/RiskFormDialog";
import { formatDate, isoDate } from "@/lib/dates";

const BUILTIN_VIEWS = [
  { key: "all", label: "All", filter: {} },
  { key: "critical", label: "Critical only", filter: { severity: "Critical" } },
  { key: "high", label: "High", filter: { severity: "High" } },
  { key: "pending", label: "Pending acceptance", filter: { status: "PendingAcceptance" } },
  { key: "accepted", label: "Accepted", filter: { status: "Accepted" } },
  { key: "overdue", label: "Overdue review", filter: { overdue: true } },
];

const ALL_COLS = [
  { key: "code",     label: "Code",     always: true },
  { key: "title",    label: "Title",    always: true },
  { key: "category", label: "Category", default: true },
  { key: "owner",    label: "Owner",    default: true },
  { key: "severity", label: "Severity", default: true },
  { key: "score",    label: "Score",    default: true },
  { key: "status",   label: "Status",   default: true },
  { key: "review",   label: "Review",   default: false },
  { key: "tags",     label: "Tags",     default: false },
  { key: "updated",  label: "Updated",  default: false },
];

const LS_VIEWS = "riskeye.savedViews";
const LS_COLS  = "riskeye.registerCols";

const KANBAN_STATUSES = ["Active", "PendingAcceptance", "Accepted", "Mitigated", "Closed"];

const readLocal = (k, fb) => {
  try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : fb; }
  catch (err) { console.warn(`Risks: failed to read localStorage key ${k}`, err); return fb; }
};
const writeLocal = (k, v) => {
  try { localStorage.setItem(k, JSON.stringify(v)); }
  catch (err) { console.warn(`Risks: failed to write localStorage key ${k}`, err); }
};

export default function Risks() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const { can } = useAuth();
  const [risks, setRisks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("q") || "");
  const [view, setView] = useState("all");
  const [severity, setSeverity] = useState(params.get("severity") || "");
  const [status, setStatus] = useState(params.get("status") || "");
  const [category, setCategory] = useState("");
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [openForm, setOpenForm] = useState(false);
  const [registers, setRegisters] = useState([]);
  const [mode, setMode] = useState("table"); // 'table' | 'kanban'
  const [projectsByRisk, setProjectsByRisk] = useState({});

  // saved views (localStorage)
  const [savedViews, setSavedViews] = useState(() => readLocal(LS_VIEWS, []));

  // columns
  const defaultCols = ALL_COLS.filter((c) => c.always || c.default).map((c) => c.key);
  const [visibleCols, setVisibleCols] = useState(() => readLocal(LS_COLS, defaultCols));
  const [showColPicker, setShowColPicker] = useState(false);

  // bulk-select
  const [selected, setSelected] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(null); // 'status'|'category'|'owner'|'delete'
  const [bulkValue, setBulkValue] = useState("");

  // save-view dialog
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState("");

  // csv import
  const fileRef = useRef(null);

  const likelihood = params.get("likelihood");
  const impact = params.get("impact");
  const breachOnly = params.get("breach") === "1";

  const load = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (q) qs.set("q", q);
      if (severity) qs.set("severity", severity);
      if (status) qs.set("status", status);
      if (category) qs.set("category", category);
      const { data } = await api.get(`/risks?${qs}`);
      setRisks(data);
      setSelected(new Set());
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [severity, status, category]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { api.get("/registers").then((r) => setRegisters(r.data)).catch(() => {}); }, []);
  useEffect(() => { writeLocal(LS_COLS, visibleCols); }, [visibleCols]);
  useEffect(() => { writeLocal(LS_VIEWS, savedViews); }, [savedViews]);

  // Fetch linked-projects for every visible risk in one Promise.all so the
  // "delivery" chip on the row is cheap. Skips if the projects endpoint 403s.
  useEffect(() => {
    if (!risks.length) return;
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(risks.map(async (r) => {
        try {
          const { data } = await api.get(`/projects/by-risk/${r.id}`);
          return [r.id, data || []];
        } catch { return [r.id, []]; }
      }));
      if (!cancelled) setProjectsByRisk(Object.fromEntries(entries));
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [risks]);

  const filtered = useMemo(() => {
    let rows = risks;
    if (likelihood && impact) rows = rows.filter((r) => r.current.likelihood === +likelihood && r.current.impact === +impact);
    if (overdueOnly) rows = rows.filter((r) => r.is_overdue_review);
    if (breachOnly) rows = rows.filter((r) => r.appetite_breach && r.status !== "Closed");
    return rows;
  }, [risks, likelihood, impact, overdueOnly, breachOnly]);

  const categories = useMemo(() => Array.from(new Set(risks.map((r) => r.category))).sort(), [risks]);

  const applyView = (v) => {
    setView(v.key);
    setSeverity(v.filter.severity || "");
    setStatus(v.filter.status || "");
    setCategory(v.filter.category || "");
    setOverdueOnly(!!v.filter.overdue);
  };

  const currentFilter = () => ({ q, severity, status, category, overdue: overdueOnly });

  const saveCurrentView = () => {
    if (!saveName.trim()) return;
    const key = `u_${Date.now()}`;
    setSavedViews([...savedViews, { key, label: saveName.trim(), filter: currentFilter(), user: true }]);
    setSaveOpen(false); setSaveName("");
    toast.success("View saved");
  };
  const deleteSavedView = (key) => setSavedViews(savedViews.filter((v) => v.key !== key));

  const clearCell = () => { params.delete("likelihood"); params.delete("impact"); setParams(params); };

  const toggleSelect = (id) => { const n = new Set(selected); n.has(id) ? n.delete(id) : n.add(id); setSelected(n); };
  const toggleAll = () => setSelected(selected.size === filtered.length ? new Set() : new Set(filtered.map((r) => r.id)));

  // ---- Bulk ----
  const runBulk = async (action, value = null, owner = null) => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    try {
      const body = { ids, action };
      if (value != null) body.value = value;
      if (owner) { body.owner_id = owner.id; body.owner_name = owner.name; }
      const { data } = await api.post("/risks/bulk", body);
      toast.success(`${action.replace("_", " ")} · ${data.affected} risk(s)`);
      setBulkOpen(null); setBulkValue(""); setSelected(new Set());
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  // ---- CSV ----
  const exportCsv = () => {
    if (!filtered.length) return;
    const header = ["code", "title", "statement", "category", "owner_name", "current_severity", "current_score", "status", "likelihood", "impact", "tags", "updated_at", "days_since_review"];
    const escape = (v) => { if (v == null) return ""; const s = typeof v === "string" ? v : Array.isArray(v) ? v.join(";") : String(v); return `"${s.replace(/"/g, '""')}"`; };
    const rows = filtered.map((r) => header.map((h) => {
      if (h === "likelihood") return escape(r.current?.likelihood);
      if (h === "impact")     return escape(r.current?.impact);
      return escape(r[h]);
    }).join(","));
    const csv = [header.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `risks_${isoDate()}_${filtered.length}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} risk(s)`);
  };

  // ---- CSV Import Preview ----
  const [importPreview, setImportPreview] = useState(null); // {rows: [{data, errors, valid}], validCount, invalidCount}
  const [importing, setImporting] = useState(false);

  const parseCsv = (text) => {
    const rows = []; let row = [], field = "", inQ = false;
    for (let i = 0; i < text.length; i++) {
      const c = text[i];
      if (inQ) {
        if (c === '"' && text[i + 1] === '"') { field += '"'; i++; }
        else if (c === '"') inQ = false;
        else field += c;
      } else {
        if (c === '"') inQ = true;
        else if (c === ',') { row.push(field); field = ""; }
        else if (c === '\n' || c === '\r') {
          if (field || row.length) { row.push(field); rows.push(row); row = []; field = ""; }
          if (c === '\r' && text[i + 1] === '\n') i++;
        } else field += c;
      }
    }
    if (field || row.length) { row.push(field); rows.push(row); }
    return rows;
  };

  const validateRow = (r) => {
    const errs = [];
    if (!r.title?.trim()) errs.push("Title is required");
    const l = Number(r.likelihood); const im = Number(r.impact);
    if (!Number.isInteger(l) || l < 1 || l > 5) errs.push(`Likelihood must be 1–5 (got "${r.likelihood || "—"}")`);
    if (!Number.isInteger(im) || im < 1 || im > 5) errs.push(`Impact must be 1–5 (got "${r.impact || "—"}")`);
    if (r.title && r.title.length > 240) errs.push("Title > 240 chars");
    if (r.category && r.category.length > 80) errs.push("Category > 80 chars");
    return errs;
  };

  const onCsvImport = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const text = reader.result.replace(/^\uFEFF/, "");
        const rows = parseCsv(text);
        if (!rows.length) { toast.error("CSV appears empty"); return; }
        const headers = rows[0].map((h) => (h || "").toLowerCase().trim());
        const parsed = rows.slice(1).filter((r) => r.some((v) => v && v.trim())).map((row, i) => {
          const data = {}; headers.forEach((h, idx) => data[h] = (row[idx] || "").trim());
          const errors = validateRow(data);
          return { i: i + 2, data, errors, valid: errors.length === 0 }; // +2 = header + 1-based
        });
        setImportPreview({
          fileName: f.name,
          rows: parsed,
          validCount: parsed.filter((r) => r.valid).length,
          invalidCount: parsed.filter((r) => !r.valid).length,
          headers,
        });
      } catch (err) { toast.error(`Could not read CSV: ${err.message}`); }
    };
    reader.readAsText(f);
    if (fileRef.current) fileRef.current.value = "";
  };

  const confirmImport = async () => {
    if (!importPreview) return;
    const valid = importPreview.rows.filter((r) => r.valid);
    if (!valid.length) { toast.error("No valid rows to import"); return; }
    setImporting(true);
    try {
      // Rebuild a clean CSV with just valid rows and post it.
      const header = importPreview.headers;
      const escape = (v) => `"${String(v || "").replace(/"/g, '""')}"`;
      const csv = [header.join(","), ...valid.map((r) => header.map((h) => escape(r.data[h])).join(","))].join("\n");
      const blob = new Blob([csv], { type: "text/csv" });
      const form = new FormData(); form.append("file", blob, "import.csv");
      const reg = registers[0]?.id ? `?register_id=${registers[0].id}` : "";
      const { data } = await api.post(`/risks/import-csv${reg}`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.created} risk(s)${data.errors?.length ? ` · ${data.errors.length} server error(s)` : ""}`);
      setImportPreview(null);
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setImporting(false); }
  };

  const isColOn = (k) => visibleCols.includes(k);
  const toggleCol = (k) => setVisibleCols(isColOn(k) ? visibleCols.filter((c) => c !== k) : [...visibleCols, k]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <PageHeader
        kicker="Risk register"
        title="Risk Register"
        description="Filter, search, bulk-edit, import/export and switch between table and kanban views."
        actions={
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5">
              <button onClick={() => setMode("table")} className={`px-2.5 py-1 rounded text-xs font-medium ${mode === "table" ? "bg-slate-900 text-white" : "text-slate-600"}`} data-testid="mode-table"><Table2 className="h-3.5 w-3.5" /></button>
              <button onClick={() => setMode("kanban")} className={`px-2.5 py-1 rounded text-xs font-medium ${mode === "kanban" ? "bg-slate-900 text-white" : "text-slate-600"}`} data-testid="mode-kanban"><LayoutGrid className="h-3.5 w-3.5" /></button>
            </div>
            <input ref={fileRef} type="file" accept=".csv" onChange={onCsvImport} className="hidden" data-testid="csv-import-input" />
            {can("risk.write") && (
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    const res = await api.get("/risks/template", { responseType: "blob" });
                    const url = URL.createObjectURL(res.data);
                    const a = document.createElement("a"); a.href = url; a.download = "riskeye_risks_template.csv"; a.click();
                    URL.revokeObjectURL(url);
                    toast.success("Template downloaded");
                  } catch (e) { toast.error(apiError(e)); }
                }}
                data-testid="btn-csv-template"
                title="Download CSV template with example rows"
              >
                <Download className="h-3.5 w-3.5 mr-1.5" /> Template
              </Button>
            )}
            {can("risk.write") && (
              <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="btn-import-csv"><Upload className="h-3.5 w-3.5 mr-1.5" /> Import CSV</Button>
            )}
            <Button variant="outline" size="sm" onClick={exportCsv} disabled={!filtered.length} data-testid="btn-export-csv"><Download className="h-3.5 w-3.5 mr-1.5" /> Export CSV</Button>
            {can("risk.write") && (
              <Button className="bg-[#0A1329] hover:bg-[#162347] text-white" onClick={() => setOpenForm(true)} data-testid="btn-new-risk"><Plus className="h-4 w-4 mr-1.5" /> New Risk</Button>
            )}
          </div>
        }
      />

      {/* Toolbar */}
      <Card className="p-4 mb-4 border-slate-200/80">
        <div className="flex flex-wrap gap-2 mb-3 items-center" data-testid="saved-views">
          {BUILTIN_VIEWS.map((v) => (
            <button key={v.key} onClick={() => applyView(v)}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors ${view === v.key ? "bg-[#0A1329] text-white border-[#0A1329]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}
              data-testid={`view-${v.key}`}>{v.label}</button>
          ))}
          {savedViews.map((v) => (
            <div key={v.key} className={`inline-flex items-center rounded-md border ${view === v.key ? "bg-cyan-700 text-white border-cyan-700" : "bg-white text-slate-600 border-slate-200"}`}>
              <button onClick={() => applyView(v)} className="pl-3 pr-1.5 py-1.5 text-xs font-semibold" data-testid={`view-${v.key}`}><Star className="h-3 w-3 inline mr-1" />{v.label}</button>
              <button onClick={() => deleteSavedView(v.key)} className="px-1.5 py-1.5 hover:text-rose-400" aria-label="Delete view"><X className="h-3 w-3" /></button>
            </div>
          ))}
          <button onClick={() => setSaveOpen(true)} className="px-3 py-1.5 rounded-md text-xs font-semibold border border-dashed border-slate-300 text-slate-500 hover:border-slate-500 hover:text-slate-800" data-testid="btn-save-view"><StarOff className="h-3 w-3 inline mr-1" /> Save view</button>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <div className="flex-1 min-w-[200px]">
            <Input placeholder="Search title, code, statement…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} data-testid="risks-search" />
          </div>
          <Select value={severity || "all"} onValueChange={(v) => setSeverity(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[140px]" data-testid="filter-severity"><SelectValue placeholder="Severity" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All severities</SelectItem>
              {["Low", "Moderate", "High", "Critical"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[170px]" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {KANBAN_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={category || "all"} onValueChange={(v) => setCategory(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[160px]" data-testid="filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          {mode === "table" && (
            <div className="relative">
              <Button variant="outline" size="sm" onClick={() => setShowColPicker(!showColPicker)} data-testid="btn-cols"><Columns className="h-3.5 w-3.5 mr-1.5" /> Columns</Button>
              {showColPicker && (
                <div className="absolute right-0 top-full mt-1 z-20 bg-white border border-slate-200 rounded-md shadow-lg p-3 w-56" data-testid="col-picker">
                  <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500 font-semibold mb-2">Show columns</div>
                  {ALL_COLS.map((c) => (
                    <label key={c.key} className={`flex items-center gap-2 py-1 text-sm cursor-pointer ${c.always ? "text-slate-400 cursor-not-allowed" : "text-slate-700"}`}>
                      <Checkbox checked={isColOn(c.key)} disabled={c.always} onCheckedChange={() => !c.always && toggleCol(c.key)} data-testid={`col-${c.key}`} />
                      {c.label}
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}
          <Button variant="ghost" size="sm" onClick={load}>Refresh</Button>
        </div>
        {(likelihood && impact) && (
          <div className="mt-3 flex items-center gap-2">
            <span className="text-xs text-slate-500">Heatmap filter:</span>
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-mono font-semibold bg-cyan-50 text-cyan-800 border border-cyan-200">
              L{likelihood} × I{impact}
              <button onClick={clearCell} className="hover:text-cyan-900"><X className="h-3 w-3" /></button>
            </span>
          </div>
        )}
      </Card>

      {/* Bulk bar */}
      {selected.size > 0 && (
        <Card className="p-3 mb-4 border-cyan-300 bg-cyan-50" data-testid="bulk-bar">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-cyan-900">{selected.size} selected</span>
            <span className="text-cyan-400">·</span>
            <Button size="sm" variant="outline" onClick={() => setBulkOpen("status")} data-testid="bulk-status">Set status</Button>
            <Button size="sm" variant="outline" onClick={() => setBulkOpen("category")} data-testid="bulk-category">Set category</Button>
            <Button size="sm" variant="outline" onClick={() => setBulkOpen("owner")} data-testid="bulk-owner">Set owner</Button>
            <Button size="sm" variant="outline" className="text-rose-700 hover:text-rose-800" onClick={() => setBulkOpen("delete")} data-testid="bulk-delete"><Trash2 className="h-3.5 w-3.5 mr-1.5" />Delete</Button>
            <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} className="ml-auto"><X className="h-3.5 w-3.5 mr-1.5" />Clear</Button>
          </div>
        </Card>
      )}

      {loading ? <LoadingSkeleton /> : filtered.length === 0 ? (
        <EmptyState icon={ShieldAlert} title="No risks match your filters" description="Adjust filters or create a new risk to begin tracking." />
      ) : mode === "kanban" ? (
        <KanbanBoard risks={filtered} onMove={async (r, s) => {
          const doPatch = (risk) => api.patch(`/risks/${risk.id}`, { status: s, version: risk.version });
          try {
            await doPatch(r); load(); toast.success(`Moved to ${s}`);
          } catch (e) {
            if (e?.response?.status === 409) {
              // Stale optimistic lock — refetch that risk and retry once.
              try {
                const { data: fresh } = await api.get(`/risks/${r.id}`);
                await doPatch(fresh); load(); toast.success(`Moved to ${s}`);
              } catch (e2) { toast.error(apiError(e2)); }
            } else {
              toast.error(apiError(e));
            }
          }
        }} onOpen={(r) => nav(`/risks/${r.id}`)} />
      ) : (
        <Card className="overflow-hidden border-slate-200/80">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="risks-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left">
                  <th className="px-3 py-2.5 w-8"><Checkbox checked={selected.size > 0 && selected.size === filtered.length} onCheckedChange={toggleAll} data-testid="row-select-all" /></th>
                  {ALL_COLS.filter((c) => isColOn(c.key)).map((c) => (
                    <th key={c.key} className="px-4 py-2.5 text-[10px] uppercase tracking-widest font-mono font-semibold text-slate-500">{c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100 hover:bg-cyan-50/40 transition-colors" data-testid={`row-risk-${r.code}`}>
                    <td className="px-3 py-3 w-8" onClick={(e) => e.stopPropagation()}><Checkbox checked={selected.has(r.id)} onCheckedChange={() => toggleSelect(r.id)} data-testid={`row-select-${r.code}`} /></td>
                    {isColOn("code")     && <td onClick={() => nav(`/risks/${r.id}`)} className="px-4 py-3 font-mono font-bold text-slate-900 tabular-nums cursor-pointer">{r.code}</td>}
                    {isColOn("title")    && <td onClick={() => nav(`/risks/${r.id}`)} className="px-4 py-3 max-w-md cursor-pointer">
                      <div className="flex items-center gap-2 font-semibold text-slate-900 truncate">
                        {r.title}
                        {r.is_overdue_review && <span title={`Overdue review · ${r.days_since_review} days`} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9.5px] font-mono uppercase tracking-wider bg-rose-100 text-rose-800 border border-rose-200"><AlertOctagon className="h-2.5 w-2.5" /> Overdue</span>}
                        {(projectsByRisk[r.id] || []).length > 0 && (
                          <span
                            title={(projectsByRisk[r.id] || []).map((p) => `${p.name} (${p.status})`).join("\n")}
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9.5px] font-mono uppercase tracking-wider bg-cyan-50 text-cyan-800 border border-cyan-200"
                            data-testid={`risk-projects-chip-${r.code}`}
                            onClick={(e) => { e.stopPropagation(); nav(`/projects`); }}
                          >
                            <Rocket className="h-2.5 w-2.5" /> {(projectsByRisk[r.id] || []).length} project{(projectsByRisk[r.id] || []).length === 1 ? "" : "s"}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 truncate mt-0.5">{r.statement}</div>
                    </td>}
                    {isColOn("category") && <td onClick={() => nav(`/risks/${r.id}`)} className="px-4 py-3 text-slate-700 cursor-pointer">{r.category}</td>}
                    {isColOn("owner")    && <td onClick={() => nav(`/risks/${r.id}`)} className="px-4 py-3 text-slate-700 text-xs cursor-pointer">{r.owner_name || "—"}</td>}
                    {isColOn("severity") && <td onClick={() => nav(`/risks/${r.id}`)} className="px-4 py-3 cursor-pointer"><SeverityBadge severity={r.current_severity} /></td>}
                    {isColOn("score")    && <td onClick={() => nav(`/risks/${r.id}`)} className="px-4 py-3 font-mono tabular-nums font-bold text-slate-900 cursor-pointer">{r.current_score}</td>}
                    {isColOn("status")   && <td onClick={() => nav(`/risks/${r.id}`)} className="px-4 py-3 cursor-pointer"><StatusPill status={r.status} /></td>}
                    {isColOn("review")   && <td onClick={() => nav(`/risks/${r.id}`)} className="px-4 py-3 text-xs cursor-pointer">{r.last_reviewed_at ? <>Reviewed {formatDate(r.last_reviewed_at)}<div className="text-slate-400 font-mono text-[10.5px]">{r.days_since_review}d ago</div></> : <span className="text-slate-400">Not reviewed</span>}</td>}
                    {isColOn("tags")     && <td onClick={() => nav(`/risks/${r.id}`)} className="px-4 py-3 text-xs cursor-pointer">{(r.tags || []).slice(0, 3).map((t) => <span key={t} className="inline-block mr-1 mb-0.5 px-1.5 py-0.5 bg-slate-100 border border-slate-200 rounded text-slate-700 font-mono text-[10px]">{t}</span>)}</td>}
                    {isColOn("updated")  && <td onClick={() => nav(`/risks/${r.id}`)} className="px-4 py-3 text-xs text-slate-500 font-mono cursor-pointer">{formatDate(r.updated_at)}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {openForm && <RiskFormDialog registers={registers} onClose={() => setOpenForm(false)} onSaved={() => { setOpenForm(false); load(); }} />}

      {/* CSV Import Preview */}
      {importPreview && (
        <Dialog open={true} onOpenChange={(o) => !o && setImportPreview(null)}>
          <DialogContent className="max-w-5xl" data-testid="import-preview-dialog">
            <DialogHeader>
              <DialogTitle className="font-display flex items-center gap-2">
                <Upload className="h-4 w-4 text-cyan-700" /> Import preview · {importPreview.fileName}
              </DialogTitle>
            </DialogHeader>

            <div className="flex flex-wrap gap-3 items-center mb-3">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-800 px-2.5 py-1 text-xs font-mono uppercase tracking-widest" data-testid="preview-valid-count">
                <Check className="h-3 w-3" /> {importPreview.validCount} valid
              </span>
              {importPreview.invalidCount > 0 && (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-rose-200 bg-rose-50 text-rose-800 px-2.5 py-1 text-xs font-mono uppercase tracking-widest" data-testid="preview-invalid-count">
                  <AlertCircle className="h-3 w-3" /> {importPreview.invalidCount} with errors
                </span>
              )}
              <span className="text-xs text-slate-500">Only valid rows will be imported. Fix the source CSV and re-upload to include the rest.</span>
            </div>

            <div className="max-h-[52vh] overflow-auto rounded-md border border-slate-200 bg-white">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 sticky top-0 z-10 border-b border-slate-200">
                  <tr className="text-left">
                    <th className="px-2 py-2 w-14 text-[10px] uppercase tracking-widest font-mono font-semibold text-slate-500">Row</th>
                    <th className="px-2 py-2 w-8 text-[10px] uppercase tracking-widest font-mono font-semibold text-slate-500">✓</th>
                    <th className="px-2 py-2 text-[10px] uppercase tracking-widest font-mono font-semibold text-slate-500">Title</th>
                    <th className="px-2 py-2 text-[10px] uppercase tracking-widest font-mono font-semibold text-slate-500">Category</th>
                    <th className="px-2 py-2 text-[10px] uppercase tracking-widest font-mono font-semibold text-slate-500">L</th>
                    <th className="px-2 py-2 text-[10px] uppercase tracking-widest font-mono font-semibold text-slate-500">I</th>
                    <th className="px-2 py-2 text-[10px] uppercase tracking-widest font-mono font-semibold text-slate-500">Owner</th>
                    <th className="px-2 py-2 text-[10px] uppercase tracking-widest font-mono font-semibold text-slate-500">Issues</th>
                  </tr>
                </thead>
                <tbody>
                  {importPreview.rows.map((r) => (
                    <tr key={r.i} className={`border-b border-slate-100 ${r.valid ? "" : "bg-rose-50/40"}`} data-testid={`preview-row-${r.i}`}>
                      <td className="px-2 py-1.5 font-mono text-slate-400 tabular-nums">{r.i}</td>
                      <td className="px-2 py-1.5">
                        {r.valid
                          ? <Check className="h-3.5 w-3.5 text-emerald-600" />
                          : <AlertCircle className="h-3.5 w-3.5 text-rose-600" />}
                      </td>
                      <td className="px-2 py-1.5 text-slate-800 max-w-[240px] truncate" title={r.data.title}>{r.data.title || <em className="text-rose-500">missing</em>}</td>
                      <td className="px-2 py-1.5 text-slate-600">{r.data.category || "—"}</td>
                      <td className={`px-2 py-1.5 font-mono tabular-nums ${r.errors.some((e) => e.startsWith("Likelihood")) ? "text-rose-600 font-bold" : "text-slate-700"}`}>{r.data.likelihood || "—"}</td>
                      <td className={`px-2 py-1.5 font-mono tabular-nums ${r.errors.some((e) => e.startsWith("Impact")) ? "text-rose-600 font-bold" : "text-slate-700"}`}>{r.data.impact || "—"}</td>
                      <td className="px-2 py-1.5 text-slate-600">{r.data.owner_name || "—"}</td>
                      <td className="px-2 py-1.5 text-[11px] text-rose-700">{r.errors.join(" · ") || <span className="text-slate-400">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <DialogFooter>
              <Button variant="ghost" onClick={() => setImportPreview(null)} data-testid="preview-cancel">Cancel</Button>
              <Button
                onClick={confirmImport}
                disabled={importing || importPreview.validCount === 0}
                className="bg-[#0A1329] hover:bg-[#162347] text-white"
                data-testid="preview-confirm"
              >
                {importing ? "Importing…" : `Import ${importPreview.validCount} valid row${importPreview.validCount === 1 ? "" : "s"}`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Save view dialog */}
      {saveOpen && (
        <Dialog open={true} onOpenChange={(o) => !o && setSaveOpen(false)}>
          <DialogContent className="max-w-sm" data-testid="save-view-dialog">
            <DialogHeader><DialogTitle>Save current filter as a view</DialogTitle></DialogHeader>
            <div>
              <Label htmlFor="v-name">View name</Label>
              <Input id="v-name" value={saveName} onChange={(e) => setSaveName(e.target.value)} placeholder="e.g. My open Criticals" data-testid="save-view-name" autoFocus />
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setSaveOpen(false)}>Cancel</Button>
              <Button onClick={saveCurrentView} className="bg-[#0A1329] hover:bg-[#162347] text-white" data-testid="save-view-submit"><Save className="h-3.5 w-3.5 mr-1.5" /> Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Bulk dialogs */}
      {bulkOpen && (
        <Dialog open={true} onOpenChange={(o) => !o && setBulkOpen(null)}>
          <DialogContent className="max-w-sm" data-testid="bulk-dialog">
            <DialogHeader><DialogTitle>Bulk {bulkOpen.replace("_", " ")} · {selected.size} risk(s)</DialogTitle></DialogHeader>
            {bulkOpen === "status" && (
              <Select value={bulkValue} onValueChange={setBulkValue}>
                <SelectTrigger data-testid="bulk-status-select"><SelectValue placeholder="Choose status" /></SelectTrigger>
                <SelectContent>{KANBAN_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            )}
            {bulkOpen === "category" && (
              <Input value={bulkValue} onChange={(e) => setBulkValue(e.target.value)} placeholder="e.g. Cyber Security" data-testid="bulk-category-input" />
            )}
            {bulkOpen === "owner" && (
              <Input value={bulkValue} onChange={(e) => setBulkValue(e.target.value)} placeholder="Owner name" data-testid="bulk-owner-input" />
            )}
            {bulkOpen === "delete" && (
              <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2.5">Permanently delete {selected.size} risk(s)? This can't be undone.</div>
            )}
            <DialogFooter>
              <Button variant="ghost" onClick={() => setBulkOpen(null)}>Cancel</Button>
              {bulkOpen === "delete"
                ? <Button className="bg-rose-600 hover:bg-rose-700 text-white" onClick={() => runBulk("delete")} data-testid="bulk-confirm-delete">Delete {selected.size}</Button>
                : <Button className="bg-[#0A1329] hover:bg-[#162347] text-white" disabled={!bulkValue} onClick={() => runBulk(`set_${bulkOpen}`, bulkOpen === "owner" ? null : bulkValue, bulkOpen === "owner" ? { name: bulkValue } : null)} data-testid="bulk-confirm">Apply</Button>}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function KanbanBoard({ risks, onMove, onOpen }) {
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);
  const wasDraggingRef = useRef(false);
  const groups = KANBAN_STATUSES.map((s) => ({ status: s, items: risks.filter((r) => r.status === s) }));

  const startDrag = (e, r) => {
    setDragId(r.id);
    wasDraggingRef.current = true;
    // Firefox requires dataTransfer.setData to fire subsequent drop events.
    try {
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", r.id);
    } catch (err) {
      // Not all browsers allow setData in every context — safe to ignore.
      console.warn("Risks: dataTransfer.setData failed", err);
    }
  };
  const endDrag = () => {
    setDragId(null);
    setOverCol(null);
    // Suppress the trailing click that would otherwise navigate to /risks/:id
    setTimeout(() => { wasDraggingRef.current = false; }, 60);
  };
  const dropInto = (status) => {
    const r = risks.find((x) => x.id === dragId);
    if (r && r.status !== status) onMove(r, status);
    setDragId(null);
    setOverCol(null);
  };
  const openCard = (r) => {
    if (wasDraggingRef.current) return;
    onOpen(r);
  };

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3" data-testid="kanban-board">
      {groups.map((g) => {
        const isOver = overCol === g.status;
        return (
          <div key={g.status}
            onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}
            onDragEnter={(e) => { e.preventDefault(); setOverCol(g.status); }}
            onDragLeave={(e) => { if (e.currentTarget.contains(e.relatedTarget)) return; if (overCol === g.status) setOverCol(null); }}
            onDrop={(e) => { e.preventDefault(); dropInto(g.status); }}
            className={`rounded-lg border p-2.5 min-h-[240px] transition-colors ${isOver ? "bg-cyan-50 border-cyan-400 border-dashed" : "bg-slate-100/70 border-slate-200"}`}
            data-testid={`kanban-col-${g.status}`}>
            <div className="flex items-center justify-between mb-2 px-1">
              <div className="text-[11px] font-mono uppercase tracking-widest font-semibold text-slate-600">{g.status}</div>
              <div className="text-[10px] font-mono text-slate-400">{g.items.length}</div>
            </div>
            <div className="space-y-2">
              {g.items.map((r) => (
                <div key={r.id}
                  draggable
                  onDragStart={(e) => startDrag(e, r)}
                  onDragEnd={endDrag}
                  onClick={() => openCard(r)}
                  className={`bg-white rounded-md border border-slate-200 p-2.5 shadow-sm hover:shadow-md cursor-grab active:cursor-grabbing transition-all ${dragId === r.id ? "opacity-40" : ""}`}
                  data-testid={`kanban-card-${r.code}`}>
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="font-mono font-bold text-slate-900 text-[11px] tabular-nums">{r.code}</span>
                    <SeverityBadge severity={r.current_severity} />
                  </div>
                  <div className="text-[13px] font-semibold text-slate-900 leading-snug line-clamp-2">{r.title}</div>
                  <div className="mt-2 flex items-center justify-between text-[10.5px] text-slate-500 font-mono">
                    <span>{r.owner_name || "Unassigned"}</span>
                    <span>Score {r.current_score}</span>
                  </div>
                  {r.is_overdue_review && <div className="mt-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9.5px] font-mono uppercase tracking-wider bg-rose-100 text-rose-800 border border-rose-200"><AlertOctagon className="h-2.5 w-2.5" /> Overdue review</div>}
                </div>
              ))}
              {g.items.length === 0 && <div className={`text-xs text-center py-6 border border-dashed rounded-md ${isOver ? "text-cyan-700 border-cyan-400 bg-cyan-50" : "text-slate-400 border-slate-300"}`}>Drop here</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}
